import React from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Client } from '../../types';
import { applyDominicanIDMask } from '../../utils/validation';
import { deriveClientStatus } from './clientStatus';
import { ClientStatusBadge } from './ClientStatusBadge';
import { ClientPlanChip } from './ClientPlanChip';
import { ClientActionGroup, type ClientActionGroupProps } from './ClientActionGroup';
import { ClientMobileCard } from './ClientMobileCard';

function getInitials(name: string, lastname: string) {
  const a = (name || '').trim().charAt(0);
  const b = (lastname || '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '—';
}

function avatarTone(seed: string): string {
  const tones = [
    'bg-slate-200 text-slate-800 ring-slate-300/80',
    'bg-indigo-100 text-indigo-900 ring-indigo-200/80',
    'bg-teal-100 text-teal-900 ring-teal-200/80',
    'bg-amber-100 text-amber-900 ring-amber-200/80',
    'bg-rose-100 text-rose-900 ring-rose-200/80'
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = seed.charCodeAt(i) + ((h << 5) - h);
  return tones[Math.abs(h) % tones.length];
}

export interface ClientTableProps {
  clients: Client[];
  loading: boolean;
  emptyState: React.ReactNode;
  getActionHandlers: (client: Client) => Omit<ClientActionGroupProps, never>;
  /** Tap en card mobile → detalle (p.ej. navigate a /clients/:id) */
  onMobileOpenDetail?: (client: Client) => void;
}

function TableSkeleton() {
  return (
    <div className="animate-pulse space-y-0 divide-y divide-slate-100">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-4 sm:px-6">
          <div className="h-11 w-11 shrink-0 rounded-full bg-slate-200" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3.5 w-1/3 rounded bg-slate-200" />
            <div className="h-3 w-1/2 rounded bg-slate-100" />
          </div>
          <div className="hidden h-4 w-24 rounded bg-slate-100 md:block" />
          <div className="hidden h-6 w-16 rounded-lg bg-slate-100 lg:block" />
        </div>
      ))}
    </div>
  );
}

export const ClientTable: React.FC<ClientTableProps> = ({
  clients,
  loading,
  emptyState,
  getActionHandlers,
  onMobileOpenDetail,
}) => {
  if (loading) {
    return (
      <div className="bg-white">
        <div className="hidden md:block">
          <TableSkeleton />
        </div>
        <div className="space-y-3 p-3 md:hidden">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[118px] animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      </div>
    );
  }

  if (!clients.length) {
    return <div className="bg-white">{emptyState}</div>;
  }

  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-[920px] w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/95">
              <th className="whitespace-nowrap px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                Cliente
              </th>
              <th className="whitespace-nowrap px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                Documento
              </th>
              <th className="whitespace-nowrap px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                Gremio
              </th>
              <th className="whitespace-nowrap px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                Plan
              </th>
              <th className="whitespace-nowrap px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                Familiares
              </th>
              <th className="whitespace-nowrap px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                Estado
              </th>
              <th className="whitespace-nowrap px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-600">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {clients.map((client) => {
              const status = deriveClientStatus(client);
              const gremio = (client.gremio || '').trim();
              const famCount = client.familyMembers?.length ?? 0;
              const cedulaDisplay = applyDominicanIDMask(String(client.cedula || '').replace(/\D/g, ''));
              const handlers = getActionHandlers(client);

              return (
                <tr
                  key={client.id}
                  className="group transition-colors hover:bg-slate-50/90"
                >
                  <td className="px-5 py-4 align-middle">
                    <div className="flex min-w-0 items-start gap-3">
                      <div
                        className={clsx(
                          'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm font-bold ring-2 ring-white',
                          avatarTone(`${client.name}${client.lastname}${client.id}`)
                        )}
                      >
                        {getInitials(client.name, client.lastname)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {client.name} {client.lastname}
                        </p>
                        <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-slate-500">
                          {client.address}
                        </p>
                        {client.telefonos && (
                          <p className="mt-1 truncate text-[11px] font-medium text-slate-400">
                            {client.telefonos.split(',')[0]?.trim()}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <span
                      className="inline-block rounded-md bg-slate-50 px-2 py-1 font-mono text-sm font-medium tabular-nums tracking-tight text-slate-800 ring-1 ring-slate-200/80"
                      title={client.cedula}
                    >
                      {cedulaDisplay || client.cedula}
                    </span>
                  </td>
                  <td className="px-4 py-4 align-middle">
                    {gremio ? (
                      <span className="inline-flex max-w-[140px] truncate rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200/80">
                        {gremio}
                      </span>
                    ) : (
                      <span className="inline-flex rounded-lg bg-slate-50 px-2.5 py-1 text-xs font-medium italic text-slate-400 ring-1 ring-slate-200/60">
                        Sin gremio
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <ClientPlanChip client={client} />
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1.5 text-sm font-semibold tabular-nums text-slate-800 ring-1 ring-slate-200/80"
                      title={`${famCount} familiar(es) registrado(s)`}
                    >
                      <UserGroupIcon className="h-4 w-4 text-slate-400" aria-hidden />
                      {famCount}
                    </span>
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <ClientStatusBadge status={status} />
                  </td>
                  <td className="px-5 py-4 align-middle">
                    <ClientActionGroup {...handlers} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Móvil: cards compactas */}
      <div className="space-y-2.5 bg-[#F7F9FC] p-3 md:hidden">
        {clients.map((client) => (
          <ClientMobileCard
            key={client.id}
            client={client}
            handlers={getActionHandlers(client)}
            onOpenDetail={() =>
              onMobileOpenDetail ? onMobileOpenDetail(client) : getActionHandlers(client).onView()
            }
          />
        ))}
      </div>
    </>
  );
};
